import React, { useCallback } from 'react';

import classNames from 'classnames';
import DropPicture from 'icons/drop-picture.svg';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';

import { useBreadBoard } from 'components/contexts/Toaster';
import DragOverlay from 'components/uploader/components/DragOverlay';
import ValidationErrorMessage from 'components/uploader/components/ValidationErrorMessage';

import {
  DEFAULT_MAX_SIZE,
  FILE_TYPES,
  mimeTypes,
} from 'components/helpers/files';

const isCancellable = { default: true, compact: false, solo: false };

const iconDimensionsConfig = {
  sm: {
    iconSize: 'tw-h-10 tw-w-10',
    marginBottom: 'tw-mb-1',
  },
  md: {
    iconSize: 'tw-h-23 tw-w-23',
    marginBottom: 'tw-mb-6',
  },
};
export default function Uploader({
  centerPlaceholder = false,
  className,
  externalInputProps = {},
  fileTypes = FILE_TYPES,
  iconDimensions = 'sm',
  isFieldInvalid = false,
  maxSize = DEFAULT_MAX_SIZE,
  maxUploads = 9,
  mode = 'default',
  onCancel,
  onUpload,
  showIcon = true,
  uploadsCount,
}) {
  const breadBoard = useBreadBoard();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'uploader.Uploader',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const acceptedFileTypes = fileTypes.filter((fileType) =>
    FILE_TYPES.includes(fileType),
  );

  const acceptedMimeTypes = mimeTypes({ fileTypes: acceptedFileTypes });

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      rejectedFiles.forEach((file) => {
        breadBoard.addInedibleToast({
          fullMessage: (
            <ValidationErrorMessage
              acceptedMimeTypes={acceptedMimeTypes}
              file={file}
              maxSize={maxSize}
            />
          ),
        });
      });

      const isMaxUploadsReached =
        uploadsCount ? uploadsCount + acceptedFiles.length > maxUploads : false;

      if (isMaxUploadsReached) {
        breadBoard.addInedibleToast({
          fullMessage: (
            <Trans
              i18nKey='attachments.errors.maxUploads'
              ns='common'
              values={{ count: maxUploads }}
            />
          ),
        });
      } else {
        acceptedFiles.forEach((file) => onUpload(file));
      }
    },
    [uploadsCount],
  );

  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept: acceptedMimeTypes,
    minSize: 0,
    maxSize,
    noClick: true,
    noKeyboard: true,
    onDrop,
  });

  return (
    <div
      {...getRootProps({
        className: `attachments__item attachments__item--${mode}-mode${mode !== 'solo' ? ' attachments__item--static' : ''}${className ? ` ${className}` : ''}`,
      })}
    >
      <div
        className={classNames(
          'tw-relative tw-min-h-full tw-rounded-lg tw-border-1 tw-border-dashed tw-border-grey-300 tw-px-3 tw-py-5 tw-text-center',
          isDragActive && 'tw-border-solid tw-border-grey-400 tw-py-13',
          isFieldInvalid && 'tw-border-red-600',
        )}
      >
        <input {...getInputProps(externalInputProps)} />
        {isDragActive && <DragOverlay />}

        <div
          className={classNames(
            centerPlaceholder &&
              'tw-absolute tw-left-1/2 tw-top-1/2 tw-m-0 -tw-translate-x-1/2 -tw-translate-y-1/2',
          )}
        >
          <div className={iconDimensionsConfig[iconDimensions].marginBottom}>
            {showIcon && (
              <DropPicture
                className={iconDimensionsConfig[iconDimensions].iconSize}
              />
            )}
            <div className='tw-text-s tw-tracking-wide tw-text-grey-500'>
              {acceptedFileTypes.join(', ')}
              <br />
              {i18nT('maxFileSizeInformation', {
                maxSizeInMB: maxSize / 1000000,
              })}
            </div>
            <Trans
              components={{
                button: (
                  <FilledButton
                    className='tw-w-max tw-bg-mint-200'
                    onClick={open}
                  />
                ),
                p: <p className='tw-my-4' />,
              }}
              i18nKey='uploader.Uploader.instructions'
              ns='components'
            />
          </div>
        </div>

        {isCancellable[mode] && onCancel && uploadsCount === 0 && (
          <div>
            <a
              className='tw-font-medium tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
              data-element-name='cancel-upload'
              onClick={onCancel}
            >
              {i18nTShared('cancel')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

Uploader.propTypes = {
  className: PropTypes.string,
  externalInputProps: PropTypes.object,
  fileTypes: PropTypes.arrayOf(PropTypes.oneOf(FILE_TYPES)),
  iconDimensions: PropTypes.oneOf(['sm', 'md']),
  isFieldInvalid: PropTypes.bool,
  maxUploads: PropTypes.number,
  mode: PropTypes.string,
  onCancel: PropTypes.func,
  onUpload: PropTypes.func.isRequired,
  uploadsCount: PropTypes.number,
};
